import React, { useEffect, useState, useRef } from 'react';

export const CustomCursor: React.FC = () => {
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isPointer, setIsPointer] = useState(false);
  const [isActive, setIsActive] = useState(false);
  const [isLightBg, setIsLightBg] = useState(false);
  
  const timeoutRef = useRef<number | null>(null);
  const touchStartPos = useRef({ x: 0, y: 0 });
  const hasMoved = useRef(false);
  
  const getLuminance = (rgbString: string) => {
    const rgb = rgbString.match(/\d+/g);
    if (!rgb || rgb.length < 3) return 0;
    
    const r = parseInt(rgb[0]) / 255;
    const g = parseInt(rgb[1]) / 255;
    const b = parseInt(rgb[2]) / 255;

    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };

  const startTimer = (duration = 2000) => {
    if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    setIsActive(true);
    timeoutRef.current = window.setTimeout(() => {
      setIsActive(false);
    }, duration);
  };

  const updateInteractableState = (target: HTMLElement) => {
    if (!target) return;
    const computedStyle = window.getComputedStyle(target);
    setIsPointer(
      computedStyle.cursor === 'pointer' || 
      ['BUTTON', 'A', 'INPUT', 'TEXTAREA'].includes(target.tagName) ||
      target.closest('button') !== null ||
      target.closest('a') !== null
    );

    let currentEl: HTMLElement | null = target;
    let bgColor = 'rgba(0, 0, 0, 0)';
    while (currentEl && (bgColor === 'rgba(0, 0, 0, 0)' || bgColor === 'transparent')) {
      bgColor = window.getComputedStyle(currentEl).backgroundColor;
      currentEl = currentEl.parentElement;
    }
    const luminance = getLuminance(bgColor);
    setIsLightBg(luminance > 0.5);
  };

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      updateInteractableState(e.target as HTMLElement);
      startTimer(2000);
    };

    // Gestión de Toques en Móvil (Tap vs Scroll)
    const handleTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0];
      touchStartPos.current = { x: touch.clientX, y: touch.clientY };
      hasMoved.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      const touch = e.touches[0];
      const deltaX = Math.abs(touch.clientX - touchStartPos.current.x);
      const deltaY = Math.abs(touch.clientY - touchStartPos.current.y);
      
      // Si el movimiento supera un umbral, es un scroll, no un tap
      if (deltaX > 10 || deltaY > 10) {
        hasMoved.current = true;
        setIsActive(false); // Ocultar si empieza a hacer scroll
      }
    };

    const handleTouchEnd = (e: TouchEvent) => {
      if (!hasMoved.current) {
        const touch = e.changedTouches[0];
        setPosition({ x: touch.clientX, y: touch.clientY });
        updateInteractableState(e.target as HTMLElement);
        startTimer(2000); // Activar solo si fue un toque limpio
      }
    };

    const onMouseDown = (e: MouseEvent) => {
      setPosition({ x: e.clientX, y: e.clientY });
      startTimer(2000);
    };

    window.addEventListener('mousemove', onMouseMove);
    window.addEventListener('mousedown', onMouseDown);
    window.addEventListener('touchstart', handleTouchStart, { passive: true });
    window.addEventListener('touchmove', handleTouchMove, { passive: true });
    window.addEventListener('touchend', handleTouchEnd, { passive: true });
    document.addEventListener('mouseleave', () => setIsActive(false));

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
      window.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
      if (timeoutRef.current) window.clearTimeout(timeoutRef.current);
    };
  }, []);

  return (
    <div 
      className={`fixed pointer-events-none z-[9999] transition-opacity duration-500 ease-out ${isActive ? 'opacity-100' : 'opacity-0'}`}
      style={{ 
        left: position.x, 
        top: position.y,
        transform: `translate(-10%, -10%) ${isPointer ? 'scale(1.2) rotate(-5deg)' : 'scale(1)'}`
      }}
    >
      <div className="relative w-12 h-12">
        <img 
          src="https://www.irreverentia.eu/cursor-hand.png" 
          alt="cursor-dark" 
          className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-200 ${isLightBg ? 'opacity-0' : 'opacity-100'}`}
          style={{ imageRendering: 'pixelated' }}
        />
        <img 
          src="https://www.irreverentia.eu/cursor-hand-purple.png" 
          alt="cursor-light" 
          className={`absolute inset-0 w-full h-full object-contain transition-opacity duration-200 ${isLightBg ? 'opacity-100' : 'opacity-0'}`}
          style={{ imageRendering: 'pixelated' }}
        />
      </div>
    </div>
  );
};